import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { type AffiliateRootState } from '.';
import { fetchPublicPlans as makePublicPlansRequest } from '@/utils/api/plans';
import { showErrorNotification } from '@/store/new-order/notificationsSlice';
import { selectPlanAndProceed } from '@/store/affiliate/actions/planSelect';

import { type Plan, mapProductsArray } from '@/utils/mapProductObject';
import { filterOutWeeklyPlans } from '@/utils/planFilters';

// caused by design, no more than 3 fit into a panel
const MAX_DISPLAYED_PLANS = 3;

export interface PlansState {
  isPlansLoading: boolean;

  publicPlans: Plan[];
  selectedPlan?: Plan;

  planLoadingError?: string;
}

const initialState: PlansState = {
  isPlansLoading: true,
  publicPlans: [],
  selectedPlan: undefined,
};

export const fetchPlans = createAsyncThunk<Plan[], undefined, { state: AffiliateRootState }>(
  'fetchPublicPlans',
  async (arg, { dispatch, getState }) => {
    try {
      return await makePublicPlansRequest(getState().coupon.code).then((res) => {
        if (res.products.length < 1) {
          throw new Error('Zero plans received from API');
        }

        const plans = mapProductsArray(res.products);

        return getState().featureFlags.excludeWeeklyPlans ? filterOutWeeklyPlans(plans) : plans;
      });
    } catch (err) {
      console.error(err);
      void dispatch(showErrorNotification('We couldn’t fetch plans for you, please try again later'));

      throw err;
    }
  },
);

export const plansSlice = createSlice({
  name: 'plans',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder.addCase(fetchPlans.pending, (state) => {
      state.isPlansLoading = true;
    });
    builder.addCase(fetchPlans.fulfilled, (state, action) => {
      state.publicPlans = action.payload.slice(0, MAX_DISPLAYED_PLANS);
      state.isPlansLoading = false;
    });
    builder.addCase(fetchPlans.rejected, (state, action) => {
      state.planLoadingError = action.error.message;
    });

    builder.addCase(selectPlanAndProceed.fulfilled, (state, action) => {
      state.selectedPlan = action.payload.plan;
    });
  },
});
